import BlogArticleTemplate from '../../components/BlogArticleTemplate.tsx';

const articleData = {
  meta: {
    title: 'SEO per Piccole Imprese: 10 Strategie che Funzionano Davvero',
    description: 'Dalla ricerca keyword alla local SEO: le tecniche più efficaci per far trovare la tua attività su Google senza budget pubblicitari enormi.',
    keywords: 'SEO piccole imprese, local SEO, posizionamento Google, SEO PMI, strategie SEO, Google My Business, SEO gratis, ottimizzazione sito web',
    ogTitle: 'SEO per Piccole Imprese: 10 Strategie Efficaci',
    ogDescription: 'Tecniche SEO pratiche per far trovare la tua attività su Google senza spendere una fortuna.',
    slug: 'seo-piccole-imprese-strategie',
    publishedDate: '2026-04-28',
    category: 'Guide Pratiche',
    readTime: '11 min',
    heroImage: '/sfondi/blog.png',
    heroImageAlt: 'SEO per Piccole Imprese',
    excerpt: 'Dalla ricerca keyword alla local SEO: le tecniche più efficaci per far trovare la tua attività su Google senza budget pubblicitari enormi.'
  },

  toc: [
    { id: 'perche-seo', label: 'Perché la SEO Conta' },
    { id: 'local-seo', label: 'Local SEO' },
    { id: 'keyword', label: 'Ricerca Keyword' },
    { id: 'on-page', label: 'SEO On-Page' },
    { id: 'contenuti', label: 'Content Strategy' },
    { id: 'tecnica', label: 'SEO Tecnica' }
  ],

  sectionOneContent: (
    <>
      <p>
        Il 46% delle ricerche su Google ha un <strong>intento locale</strong>. Se hai un'attività e non compari nei risultati, stai regalando clienti alla concorrenza.
      </p>

      <p>
        La buona notizia: la SEO per piccole imprese non richiede budget enormi. Richiede <strong>strategia, costanza e le tecniche giuste</strong>. Ecco le 10 che funzionano davvero.
      </p>

      <h2 id="perche-seo">Perché la SEO Conta per le PMI</h2>

      <p>
        A differenza della pubblicità a pagamento, la SEO genera <strong>traffico organico continuativo</strong>. Una volta posizionato, il tuo sito riceve visite gratuite ogni giorno, senza pagare per ogni click.
      </p>

      <div className="info-box highlight">
        <h3>SEO vs Ads: I Numeri</h3>
        <ul>
          <li><strong>Il 70% dei click</strong> va ai risultati organici, non agli annunci</li>
          <li><strong>La SEO costa il 61% in meno</strong> per lead rispetto agli annunci a pagamento</li>
          <li><strong>I risultati durano nel tempo:</strong> un contenuto ben posizionato genera traffico per anni</li>
        </ul>
      </div>

      <h2 id="local-seo">1. Google Business Profile (ex Google My Business)</h2>

      <p>
        La prima cosa da fare, e la più impattante. Il tuo profilo Google Business è quello che appare nella <strong>mappa e nei risultati locali</strong> quando qualcuno cerca "web designer vicino a me" o "ristorante Modena".
      </p>

      <ul>
        <li><strong>Completa ogni campo:</strong> Nome, indirizzo, telefono, orari, categorie</li>
        <li><strong>Aggiungi foto reali:</strong> Del locale, dei prodotti, del team (almeno 10)</li>
        <li><strong>Pubblica aggiornamenti:</strong> Post settimanali con novità e offerte</li>
        <li><strong>Rispondi alle recensioni:</strong> Tutte, positive e negative</li>
        <li><strong>Chiedi recensioni:</strong> Ai clienti soddisfatti, con un link diretto</li>
      </ul>
    </>
  ),

  parallaxBoxes: [
    {
      title: '2. Ricerca Keyword Intelligente',
      content: (
        <>
          <p>Non puntare a keyword generiche e competitive. Concentrati sulle <strong>long-tail keyword</strong>:</p>
          <ul>
            <li><strong>Generica:</strong> "web designer" (troppo competitiva)</li>
            <li><strong>Long-tail:</strong> "web designer freelance Modena" (raggiungibile)</li>
            <li><strong>Con intento:</strong> "quanto costa un sito web per ristorante" (alta conversione)</li>
          </ul>
          <p>Strumenti gratuiti: Google Suggest, AnswerThePublic, Google Search Console.</p>
        </>
      )
    },
    {
      title: '3. Ottimizzazione On-Page',
      content: (
        <>
          <p>Ogni pagina del tuo sito deve essere ottimizzata:</p>
          <ul>
            <li><strong>Title tag:</strong> Keyword principale + nome attività (max 60 caratteri)</li>
            <li><strong>Meta description:</strong> Riassunto accattivante con call-to-action (max 155 caratteri)</li>
            <li><strong>H1 unico:</strong> Un solo H1 per pagina, con la keyword principale</li>
            <li><strong>URL parlanti:</strong> /servizi/web-design-modena, non /pagina?id=123</li>
            <li><strong>Alt text immagini:</strong> Descrittivo e con keyword dove naturale</li>
          </ul>
        </>
      )
    },
    {
      title: '4. Contenuti di Valore',
      content: (
        <>
          <p>Google premia i siti che <strong>rispondono alle domande degli utenti</strong>:</p>
          <ul>
            <li><strong>Blog aziendale:</strong> Articoli che rispondono a dubbi dei tuoi clienti</li>
            <li><strong>FAQ:</strong> Una pagina con le domande più frequenti</li>
            <li><strong>Guide pratiche:</strong> Contenuti approfonditi che dimostrano competenza</li>
            <li><strong>Case study:</strong> Risultati ottenuti con i clienti</li>
          </ul>
          <p>Pubblica con regolarità: 2-4 articoli al mese è un buon ritmo per una PMI.</p>
        </>
      )
    }
  ],

  threeBoxes: [
    {
      number: '5',
      title: 'Velocità del Sito',
      content: (
        <>
          <p>Un fattore di ranking sempre più importante:</p>
          <ul>
            <li><strong>Core Web Vitals:</strong> LCP, FID, CLS devono essere "buoni"</li>
            <li><strong>Immagini:</strong> Formato WebP, lazy loading, dimensioni corrette</li>
            <li><strong>Hosting:</strong> Server veloce, possibilmente con CDN</li>
          </ul>
        </>
      )
    },
    {
      number: '6',
      title: 'Mobile First',
      content: (
        <>
          <p>Google indicizza la versione mobile del tuo sito:</p>
          <ul>
            <li><strong>Responsive:</strong> Il sito deve adattarsi a ogni schermo</li>
            <li><strong>Touch-friendly:</strong> Pulsanti grandi, menu usabili</li>
            <li><strong>Test:</strong> Verifica con il Mobile-Friendly Test di Google</li>
          </ul>
        </>
      )
    },
    {
      number: '7',
      title: 'Link Building Locale',
      content: (
        <>
          <p>I backlink restano un fattore di ranking cruciale:</p>
          <ul>
            <li><strong>Directory locali:</strong> PagineGialle, Yelp, TripAdvisor</li>
            <li><strong>Associazioni:</strong> Camera di commercio, associazioni di categoria</li>
            <li><strong>Collaborazioni:</strong> Guest post con attività complementari</li>
          </ul>
        </>
      )
    }
  ],

  twoColumnSections: [
    {
      left: (
        <>
          <h3 id="on-page">8. Schema Markup</h3>
          <p>I <strong>dati strutturati</strong> aiutano Google a capire il tuo sito:</p>
          <ul>
            <li><strong>LocalBusiness:</strong> Nome, indirizzo, orari, telefono</li>
            <li><strong>Product:</strong> Per e-commerce (prezzo, disponibilità)</li>
            <li><strong>FAQ:</strong> Domande e risposte (appaiono nei risultati)</li>
            <li><strong>Review:</strong> Stelline nei risultati di ricerca</li>
          </ul>
          <p>Risultato: snippet più ricchi e click-through rate più alto.</p>
        </>
      ),
      right: (
        <>
          <h3>9. Google Search Console</h3>
          <p>Il tuo strumento SEO gratuito più potente:</p>
          <ul>
            <li><strong>Query di ricerca:</strong> Per cosa ti trovano gli utenti</li>
            <li><strong>Pagine indicizzate:</strong> Cosa vede Google del tuo sito</li>
            <li><strong>Errori:</strong> Pagine 404, problemi di crawling</li>
            <li><strong>Core Web Vitals:</strong> Performance reali degli utenti</li>
          </ul>
          <p>Controllalo almeno una volta a settimana.</p>
        </>
      )
    }
  ],

  infoBoxContent: (
    <>
      <h2 id="tecnica">10. Consistenza NAP</h2>
      <p>Per la local SEO, il tuo <strong>Nome, Indirizzo e Telefono</strong> (NAP) devono essere identici ovunque:</p>
      <div className="info-box">
        <ul>
          <li><strong>Sito web:</strong> Footer e pagina contatti</li>
          <li><strong>Google Business Profile:</strong> Profilo completo e aggiornato</li>
          <li><strong>Social media:</strong> Facebook, Instagram, LinkedIn</li>
          <li><strong>Directory:</strong> PagineGialle, Yelp, portali di settore</li>
          <li><strong>Citazioni:</strong> Qualsiasi menzione online della tua attività</li>
        </ul>
        <p>Anche una piccola differenza ("Via" vs "V.") può confondere Google.</p>
      </div>
    </>
  ),

  conclusionContent: (
    <>
      <p>
        La <strong>SEO per piccole imprese</strong> non è complicata, ma richiede costanza. Inizia da Google Business Profile e dall'ottimizzazione on-page, poi espandi con contenuti e link building.
      </p>
      <p>
        I risultati non arrivano in una settimana, ma in 3-6 mesi vedrai un aumento costante del traffico organico e dei contatti. È l'investimento con il miglior ritorno a lungo termine per una PMI.
      </p>
    </>
  ),

  ctaBox: {
    title: 'Vuoi Migliorare la Visibilità del Tuo Sito?',
    description: 'Realizzo siti web ottimizzati SEO che si posizionano su Google. Audit SEO gratuito incluso nel preventivo.',
    buttonText: 'Richiedi un Preventivo'
  },

  relatedPosts: [
    {
      slug: 'quanto-costa-sito-web-professionale-2025',
      title: 'Quanto costa un sito web professionale?',
      category: 'Prezzi e Costi',
      readTime: '8 min'
    },
    {
      slug: 'landing-page-efficace-come-convertire',
      title: 'Landing Page Efficace: Come Convertire',
      category: 'Conversioni',
      readTime: '10 min'
    },
    {
      slug: 'nextjs-vs-react-quale-scegliere',
      title: 'Next.js vs React: Quale Scegliere',
      category: 'Web Design',
      readTime: '9 min'
    }
  ]
};

function SeoPiccoleImprese() {
  return <BlogArticleTemplate {...articleData} />;
}

export default SeoPiccoleImprese;
